import { apiClient } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IApiResponse, IPaginatedResponse } from './../types';
import type {
    IAddBugReportComment,
    IBugReport,
    IBugReportDetail,
    IBugReportListResult,
    IBugReportQuery,
    ICreateBugReport,
    IGetBugReportUploadUrlInput,
} from '@/features/bug-reports/types';

const B = API_ENDPOINTS.BUG_REPORTS;

/**
 * Every endpoint here is reporter-scoped: the API derives the org and the user
 * from the session, so a caller only ever sees the reports they filed and can
 * only reply to those. The reports themselves live in the super admin portal —
 * this API proxies to it.
 */
export const bugReportsApi = {
    create: (input: ICreateBugReport): Promise<{ id: string }> =>
        apiClient.post<IApiResponse<{ id: string }>>(B.LIST, input).then((r) => r.data.data),

    list: (params?: IBugReportQuery): Promise<IBugReportListResult> =>
        apiClient
            .get<
                IApiResponse<readonly IBugReport[]> & { meta: IPaginatedResponse<IBugReport> }
            >(B.LIST, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    getById: (id: string): Promise<IBugReportDetail> =>
        apiClient.get<IApiResponse<IBugReportDetail>>(B.BY_ID(id)).then((r) => r.data.data),

    /** A reporter reply. Reopens the report server-side when it was closed. */
    addComment: (id: string, input: IAddBugReportComment): Promise<{ id: string }> =>
        apiClient
            .post<IApiResponse<{ id: string }>>(B.COMMENTS(id), input)
            .then((r) => r.data.data),

    getUploadUrl: (
        input: IGetBugReportUploadUrlInput,
    ): Promise<{ uploadUrl: string; storageKey: string }> =>
        apiClient
            .post<
                IApiResponse<{ uploadUrl: string; storageKey: string }>
            >(B.ATTACHMENT_UPLOAD_URL, input)
            .then((r) => r.data.data),
};
